import { Routes } from '@angular/router';
import { Documentation } from './app/pages/documentation/documentation';
import { Landing } from './app/pages/landing/landing';
import { Notfound } from './app/pages/notfound/notfound';
import { DashboardComponent } from './app/pages/dashboard/dashboard';
import { Conversation } from '@/pages/conversation/conversation';
import { Settings } from '@/pages/settings/settings';
import { AiLogic } from '@/pages/ai-logic/ai-logic';
import { Team } from '@/pages/team/team';
import { Channels } from '@/pages/channels/channels';
import { AuthGuard } from '@/pages/auth/auth.guard';
import { RoleGuard } from '@/pages/auth/role.guard';
import { seoAccessGuard } from '@/pages/seo/seo-access.guard';
import { geoAccessGuard } from '@/pages/seo/geo-access.guard';
import { Worflows } from '@/pages/worflows/worflow';
import { MyTask } from '@/pages/my-task/my-task';
import { ManagerView } from '@/pages/manager-view/manager-view';

export const appRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
            import('./app/pages/website-template/website-template').then((m) => m.WebsiteTemplateComponent)
    },
    {
        path: 'webchat-widget',
        loadComponent: () => import('./app/pages/webchat-widget/webchat-widget').then((m) => m.WebchatWidgetComponent)
    },
    {
        // Forced first-login password change — authenticated, but bare (no app shell).
        path: 'set-password',
        canActivate: [AuthGuard],
        loadComponent: () => import('./app/pages/auth/components/set-password/set-password').then((m) => m.SetPassword)
    },
    {
        path: 'webchat-center',
        loadComponent: () => import('./app/pages/webchat-center/webchat-center').then((m) => m.WebchatCenterComponent)
    },
    {
  path: 'auth',
  loadChildren: () =>
    import('./app/pages/auth/auth.routes')
      .then(m => m.authRoutes)
},
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
            { path: 'dashboard', component: DashboardComponent },
            { path: '', redirectTo: 'lead-dashboard', pathMatch: 'full' },
            { path: 'conversations', component: Conversation, canActivate: [RoleGuard],
        data: { roles: ['ROLE_AGENT', 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            { path: 'workflows', component: Worflows , canActivate: [RoleGuard],
        data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            // All roles may open Settings, but agents/managers are locked to the My Profile
            // tab (see settings.ts) — every other tab's data is admin-gated on the backend.
            { path: 'settings', component: Settings, canActivate: [RoleGuard],
        data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'] } },
            // 'roles-access' (the per-role screen grid) RETIRED — screen access is configured
            // ONLY via Permission Templates now. Route removed; the role still governs data scope
            // + behaviour. The RolesAccessComponent is left in the tree but is no longer reachable.
            { path: 'permission-templates', canActivate: [RoleGuard],
        data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () => import('./app/pages/permission-templates/permission-templates.component').then((m) => m.PermissionTemplatesComponent) },
            { path: 'ai-rules', component: AiLogic , canActivate: [RoleGuard],
        data: { roles: ['ROLE_AGENT', 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            { path: 'team', component: Team , canActivate: [RoleGuard],
        data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            { path: 'my-tasks', component: MyTask, canActivate: [RoleGuard],
        data: { roles: ['ROLE_AGENT', 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            { path: 'manager-view', component: ManagerView, canActivate: [RoleGuard],
        data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            { path: 'channels', component: Channels, canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] } },
            {
                path: 'whatsapp-templates', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/whatsapp-template-list/whatsapp-template-list')
                    .then(m => m.WhatsappTemplateList)
            },
            {
                path: 'whatsapp-templates/:id', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/whatsapp-template-list/wa-template-detail/wa-template-detail')
                    .then(m => m.WaTemplateDetail)
            },
            {
                path: 'email-sequences', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/email-sequences/email-sequences.component')
                    .then(m => m.EmailSequencesComponent)
            },

            // ðŸŸ¢ DATABASE ROUTES (Newly Added)
            {
                path: 'database/tables', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },

                loadComponent: () =>
                    import('./app/pages/database/database-component')
                    .then(m => m.DatabaseComponent)
            },

            // 🌳 CATALOG — hierarchical no-code DB (parent → child → child, typed levels)
            {
                path: 'catalogs', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/catalog/catalog-list.component').then(m => m.CatalogListComponent)
            },
            {
                path: 'catalogs/:id/design', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/catalog/catalog-designer.component').then(m => m.CatalogDesignerComponent)
            },
            {
                path: 'catalogs/:id', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/catalog/catalog-explorer.component').then(m => m.CatalogExplorerComponent)
            },
            {
                path: 'vendors', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => 
                    import('./app/pages/vendor/vendor')
                    .then(m => m.Vendor)
            },
            {
                path: 'vendors/:id', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => 
                    import('./app/pages/vendor/vendor-details')
                    .then(m => m.VendorDetails)
            },
            {
                path: 'customers', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/customer/customer')
                    .then(m => m.Customer)
            },
            {
                path: 'customers/:id', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/customer/customer-details')
                    .then(m => m.CustomerDetails)
            },
            {
                path: 'database/campaigns', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/vendors/vendors')
                    .then(m => m.Vendors)
            },
            {
                path: 'database/campaigns/:id', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/vendors/campaign-details')
                    .then(m => m.CampaignDetails)
            },

            // 📣 CAMPAIGN MANAGEMENT SYSTEM
            {
                path: 'campaigns', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/campaigns-list/campaigns-list.component')
                    .then(m => m.CampaignsListComponent)
            },
            {
                path: 'campaigns/new', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/campaign-wizard/campaign-wizard.component')
                    .then(m => m.CampaignWizardComponent)
            },
            {
                path: 'campaigns/segments', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/segments/segments.component')
                    .then(m => m.SegmentsComponent)
            },
            {
                path: 'super-admin/rate-card', canActivate: [RoleGuard],
                data: { roles: ['ROLE_SUPER_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/super-admin/rate-card.component')
                    .then(m => m.RateCardComponent)
            },
            {
                path: 'super-admin/wallets', canActivate: [RoleGuard],
                data: { roles: ['ROLE_SUPER_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/super-admin/companies-wallets.component')
                    .then(m => m.CompaniesWalletsComponent)
            },
            {
                path: 'super-admin/error-logs', canActivate: [RoleGuard],
                data: { roles: ['ROLE_SUPER_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/super-admin/error-logs.component')
                    .then(m => m.ErrorLogsComponent)
            },
            {
                path: 'super-admin/app-version', canActivate: [RoleGuard],
                data: { roles: ['ROLE_SUPER_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/super-admin/app-version.component')
                    .then(m => m.AppVersionComponent)
            },
            {
                path: 'super-admin/migration', canActivate: [RoleGuard],
                data: { roles: ['ROLE_SUPER_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/super-admin/migration.component')
                    .then(m => m.SaMigrationComponent)
            },
            {
                path: 'campaigns/ai-suggestions', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/ai-suggestions/ai-suggestions.component')
                    .then(m => m.AiSuggestionsComponent)
            },
            {
                path: 'campaigns/analytics', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/analytics/analytics.component')
                    .then(m => m.CampaignAnalyticsComponent)
            },
            {
                path: 'campaigns/:id/analytics', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/analytics/analytics.component')
                    .then(m => m.CampaignAnalyticsComponent)
            },
            {
                path: 'campaigns/suppression', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/campaigns/suppression-log/suppression-log.component')
                    .then(m => m.SuppressionLogComponent)
            },
            {
                path: 'lead-wa-configs', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/lead-whatsapp-config/lead-wa-config-list')
                    .then(m => m.LeadWaConfigList)
            },
            {
                path: 'email-templates', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/email-templates/email-template-list/email-template-list')
                    .then(m => m.EmailTemplateList)
            },
            {
                path: 'email-config', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/email-config/email-config')
                    .then(m => m.EmailConfigComponent)
            },
            {
                path: 'email-templates/editor', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/email-templates/email-template-editor/email-template-editor')
                    .then(m => m.EmailTemplateEditor)
            },
            {
                path: 'email-templates/editor/:id', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/email-templates/email-template-editor/email-template-editor')
                    .then(m => m.EmailTemplateEditor)
            },
            {
                path: 'email-automation', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/lead-email-automation/lead-email-automation.component')
                    .then(m => m.LeadEmailAutomationComponent)
            },
            {
                path: 'whatsapp-automation', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/whatsapp-automation/whatsapp-automation.component')
                    .then(m => m.WhatsAppAutomationComponent)
            },
            // 📣 The old /ad-feedback dashboard was retired on 2026-09-12. It held three things:
            //   · connection + standard signals  → already in the wizard, steps 1 and 3
            //   · funnel mapping                 → moved into step 3, next to the outcomes it replaces
            //   · the send ledger                → moved to /logs?tab=meta-capi with the other logs
            // Two cards for one integration was the confusing part. The component was moved to
            // growhigh-prod-env/removed/ rather than deleted, since this project has no git.
            // 🔗 META SETUP WIZARD — the guided path from "nothing" to "leads arriving and outcomes
            // reported back": share assets → map forms → dataset routing → test & go live. Admin-only
            // because it drives the company's Meta connection (/api/config/v1/meta-connection/**,
            // COMPANY_ADMIN on the backend too).
            {
                path: 'meta-setup', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/meta-setup/meta-setup.component')
                    .then(m => m.MetaSetupComponent)
            },
            // ☎️ VOICE & TELEPHONY — click-to-call, the smart dialer, and routing an inbound
            // caller to the agent who owns their case. Admin-only: it holds the provider
            // credentials and decides which number the whole company calls from.
            // ⚠️ MOCKUP as of 2026-09-14 — the screens render from in-memory sample data and
            // nothing is persisted. See voice-setup.model.ts.
            {
                path: 'voice-setup', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/voice-setup/voice-setup.component')
                    .then(m => m.VoiceSetupComponent)
            },
            // 📲 MOBILE EVENTS — map app events → journey actions + tokens
            {
                path: 'event-mappings', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/event-mappings/event-mappings.component')
                    .then(m => m.EventMappingsComponent)
            },
            // 📲 EVENT QUEUE — unmatched / unmapped events + replay
            {
                path: 'event-queue', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/event-queue/event-queue.component')
                    .then(m => m.EventQueueComponent)
            },
            // 📜 LOGS HUB — categorised tabs: Bulk Sends + Event Queue + the three audit feeds.
            // The old /bulk-send-log and /event-queue routes stay for deep links; the menu points here.
            {
                path: 'logs', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/logs-hub/logs-hub.component')
                    .then(m => m.LogsHubComponent)
            },
            // 📤 BULK SENDS — per-recipient status log for bulk WhatsApp/Email sends
            {
                path: 'bulk-send-log', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/bulk-send-log/bulk-send-log.component')
                    .then(m => m.BulkSendLogComponent)
            },
            // 💰 COMMISSION — per-team vendor commission rules (super distributor / distributor / agent)
            {
                path: 'commission', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/commission/commission.component')
                    .then(m => m.CommissionComponent)
            },
            // 💰 COMMISSION OVERVIEW — main-menu report: restricted teams' leads created + commission earned, per user
            {
                path: 'commission-overview', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/commission-overview/commission-overview.component')
                    .then(m => m.CommissionOverviewComponent)
            },
            // ✅ PHASE REVIEWS — maker-checker bucket: managers approve/reject parked advances
            {
                path: 'phase-reviews', canActivate: [RoleGuard],
                data: { roles: ['ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/phase-reviews/phase-reviews.component')
                    .then(m => m.PhaseReviewsComponent)
            },
            // ✨ AUTOPILOT BOARD — team coaching lens over agent_metrics_daily + the mode switch (Phase 5b)
            {
                path: 'autopilot-board', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/autopilot-board/autopilot-board.component')
                    .then(m => m.AutopilotBoardComponent)
            },
            // 🎯 DEAL FORECAST — pipeline KPIs, period forecasts, hot/warm/cold triage (both editions)
            {
                path: 'deal-forecast', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/deal-forecast/deal-forecast.component')
                    .then(m => m.DealForecastComponent)
            },
            // 📊 REPORTS — self-serve report builder, email schedules, Loss Cause AI
            {
                path: 'deal-reports', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/deal-reports/deal-reports.component')
                    .then(m => m.DealReportsComponent)
            },
            // ✨ ASSISTANT — in-app setup assistant (chat + confirmed configuration changes)
            {
                path: 'assistant', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/assistant/assistant-page.component')
                    .then(m => m.AssistantPageComponent)
            },
            // 📱 MOBILE APP CONFIG — GrowHigh Agent app: call recording + call-log sync scope
            {
                path: 'mobile-app-config', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/mobile-app-config/mobile-app-config.component')
                    .then(m => m.MobileAppConfigComponent)
            },
            // 📅 CALENDAR — personal schedule (own events + surfaced tasks), all roles.
            {
                path: 'calendar', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/calendar/calendar.component')
                    .then(m => m.CalendarComponent)
            },
            // ✅ ROUTINES — personal coached routine planner (dedicated view), all roles.
            {
                path: 'routines', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/routines/routines.component')
                    .then(m => m.RoutinesComponent)
            },
            // ⚡ ACTIVE LEADS — leads ranked by conversion potential (manager/admin).
            {
                path: 'active-leads', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/active-leads/active-leads.component')
                    .then(m => m.ActiveLeadsComponent)
            },
            // ⚙️ OPERATIONS — sequence journey phases + map workflows
            {
                path: 'operations', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/operations/operations.component')
                    .then(m => m.OperationsComponent)
            },
            // 📊 OPERATIONS MANAGER DASHBOARD — SLA breaches + status breakdown
            {
                path: 'operations-dashboard', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/operations-dashboard/operations-dashboard.component')
                    .then(m => m.OperationsDashboardComponent)
            },
            // Phase 7 — Find customer + claim-request workflow. Same page serves
            // agents (Find + My requests) and managers (+ Inbox tab).
            {
                path: 'claim-customer', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/claim/claim-customer.component')
                    .then(m => m.ClaimCustomerComponent)
            },
            // 🧑‍💼 MY WORK — same dashboard, scoped to the caller (agent: own cases;
            //    manager: their department). Rows open the case filtered to the agent's phases.
            {
                path: 'my-work', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'], mode: 'myWork' },
                loadComponent: () =>
                    import('./app/pages/operations-dashboard/operations-dashboard.component')
                    .then(m => m.OperationsDashboardComponent)
            },
            // 📈 PERFORMANCE — agent's own activity + outcomes; managers see team totals + leaderboard.
            {
                path: 'performance', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/performance/performance.component')
                    .then(m => m.PerformanceComponent)
            },
            // 👤 MY PROFILE — self-service for any user (name/phone/designation, password, avatar)
            {
                path: 'my-profile', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN'] },
                loadComponent: () =>
                    import('./app/pages/my-profile/my-profile.component')
                    .then(m => m.MyProfileComponent)
            },
            // 📍 FIELD TRACKING — manager replays an agent's GPS route for a chosen day
            {
                path: 'field-tracking', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/field-tracking/field-tracking.component')
                    .then(m => m.FieldTrackingComponent)
            },
            // 🕒 ATTENDANCE — check-in/out + selfies + GPS, daily/weekly per agent
            {
                path: 'attendance', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/attendance/attendance.component')
                    .then(m => m.AttendanceComponent)
            },
            // 📈 OPERATIONS REPORT — analytics snapshot (KPIs, breakdowns, trend)
            {
                path: 'operations-reports', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/operations-reports/operations-reports.component')
                    .then(m => m.OperationsReportsComponent)
            },
            // 🗂️ OPERATION CASE — agent workspace (phase journey + SOPs + actions)
            {
                path: 'operation-case/:caseId', canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/operation-case/operation-case.component')
                    .then(m => m.OperationCaseComponent)
            },
            {
                path: 'bulk-uploads', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/bulk-upload/bulk-upload')
                    .then(m => m.BulkUpload)
            },
            // 📥 LEAD IMPORT — CSV → lead + operation case per row (routed by Contact Source)
            {
                path: 'lead-import', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/lead-import/lead-import')
                    .then(m => m.LeadImport)
            },
            {
                path: 'database/contact', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => 
                    import('./app/pages/contact-list/contact-list')
                    .then(m => m.ContactList)
            },
            {
                path: 'database/tables/:id', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                
                loadComponent: () => 
                    import('./app/pages/database/database-details/database-details-component')
                    .then(m => m.DatabaseDetailsComponent)
            },

            // ðŸ”„ WORKFLOW DETAILS (Lazy Loaded)
            {
                path: 'workflows/:companyId/:flowName', canActivate: [RoleGuard],
        data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/worflows/worflow-details/worflow-details')
                    .then(m => m.WorflowDetails)
            },

            // 🗺️ JOURNEY CREATE
            {
                path: 'journeys/new', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/worflows/journey-create/journey-create.component')
                    .then(m => m.JourneyCreateComponent)
            },

            // 📊 JOURNEY ANALYTICS
            {
                path: 'journey-analytics', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/journey-analytics/journey-analytics.component')
                    .then(m => m.JourneyAnalyticsComponent)
            },

            // ðŸ”„ CONVERSATION DETAILS (Converted to Lazy Loading)
            // RETIRED 2026-09-14. The old full-page conversation view, superseded by the
            // three-column /conversations screen. It was still routed but NOTHING in the app
            // linked to it - reachable only by typing the URL - and its scroll-to-newest call
            // had been commented out, so it did not behave like the live inbox either.
            //
            // Commented rather than deleted: the component + template (1,042 lines) are still
            // on disk under components/conversation-details/, and this block is all that is
            // needed to bring it back. Note tsconfig.app.json builds from
            // `files: ["src/main.ts"]`, so with this import gone the component is no longer
            // part of the bundle at all.
            // {
            // path: 'conversation-details', canActivate: [RoleGuard],
            // data: { roles: [ 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'
            // ] },
            // loadComponent: () =>
            // import('@/pages/conversation/components/conversation-details/conversation-details')
            // .then(m => m.ConversationDetails)
            // },

            // 🔔 REMINDERS
            {
                path: 'reminders',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/reminders/reminders.component')
                    .then(m => m.RemindersComponent)
            },

            // 🏥 REFERRAL MANAGEMENT CONFIG
            {
                path: 'referral-config',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/referral-config/referral-config.component')
                    .then(m => m.ReferralConfigComponent)
            },

            // 📅 APPOINTMENTS
            {
                path: 'appointments',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_AGENT', 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/appointments/appointments.component')
                    .then(m => m.AppointmentsComponent)
            },

            // 🎯 LEADS
            {
                path: 'lead-dashboard',
                loadComponent: () =>
                    import('./app/pages/leads/components/lead-dashboard/lead-dashboard.component')
                    .then(m => m.LeadDashboardComponent)
            },
            {
                path: 'leads',
                loadComponent: () =>
                    import('./app/pages/leads/components/leads-list/leads-list.component')
                    .then(m => m.LeadsListComponent)
            },
            {
                path: 'lead-insights',
                loadComponent: () =>
                    import('./app/pages/leads/components/lead-insights/lead-insights.component')
                    .then(m => m.LeadInsightsComponent)
            },
            {
                path: 'leads/:id/journey',
                loadComponent: () =>
                    import('./app/pages/leads/components/lead-journey/lead-journey.component')
                    .then(m => m.LeadJourneyComponent)
            },

            // 👥 CUSTOMER FLOWS (per-phone conversation states)
            {
                path: 'customer-flows', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'] },
                loadComponent: () =>
                    import('./app/pages/customer-flows/customer-flows-list.component')
                    .then(m => m.CustomerFlowsListComponent)
            },
            {
                path: 'customer-flows/:waId', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'] },
                loadComponent: () =>
                    import('./app/pages/leads/components/lead-journey/lead-journey.component')
                    .then(m => m.LeadJourneyComponent)
            },

            // 🛟 SUPPORT (workflow-centric case view)
            {
                path: 'support', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'] },
                loadComponent: () =>
                    import('./app/pages/support/support-workflows.component')
                    .then(m => m.SupportWorkflowsComponent)
            },
            {
                path: 'support/workflows/:name', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER', 'ROLE_AGENT'] },
                loadComponent: () =>
                    import('./app/pages/support/support-workflow-cases.component')
                    .then(m => m.SupportWorkflowCasesComponent)
            },

            // 🌐 WEBSITE BUILDER
            {
                path: 'website-builder',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/website-builder/website-list/website-list.component')
                    .then(m => m.WebsiteListComponent)
            },
            {
                path: 'website-builder/templates',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/website-builder/template-gallery/template-gallery.component')
                    .then(m => m.TemplateGalleryComponent)
            },
            {
                path: 'website-builder/edit/:id',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/website-builder/website-editor/website-editor.component')
                    .then(m => m.WebsiteEditorComponent)
            },
            {
                path: 'website-builder/preview/:id',
                canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () =>
                    import('./app/pages/website-builder/website-preview/website-preview.component')
                    .then(m => m.WebsitePreviewComponent)
            },

            // 🔍 SEO ENGINE — per-company add-on (proxied to growhigh-blog). Company context
            // comes from the JWT; seoAccessGuard gates on enabled + onboarded. The super admin
            // turns it on per company from Companies & Wallets.
            { path: 'seo', redirectTo: 'seo/dashboard', pathMatch: 'full' },
            {
                path: 'seo/dashboard', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-dashboard.component').then(m => m.SeoDashboardComponent)
            },
            {
                path: 'seo/posts', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-posts.component').then(m => m.SeoPostsComponent)
            },
            {
                path: 'seo/keywords', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-keywords.component').then(m => m.SeoKeywordsComponent)
            },
            {
                path: 'seo/audit', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-audit.component').then(m => m.SeoAuditComponent)
            },
            {
                path: 'seo/suggestions', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-suggestions.component').then(m => m.SeoSuggestionsComponent)
            },
            {
                path: 'seo/local-map', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-local-map.component').then(m => m.SeoLocalMapComponent)
            },
            {
                path: 'seo/ai-visibility', canActivate: [RoleGuard, geoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-ai-visibility.component').then(m => m.SeoAiVisibilityComponent)
            },
            {
                path: 'seo/report', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-report.component').then(m => m.SeoReportComponent)
            },
            {
                path: 'seo/settings', canActivate: [RoleGuard, seoAccessGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN'] },
                loadComponent: () => import('./app/pages/seo/seo-settings.component').then(m => m.SeoSettingsComponent)
            },
            {
                path: 'seo/onboarding', canActivate: [RoleGuard],
                data: { roles: ['ROLE_COMPANY_ADMIN', 'ROLE_MANAGER'] },
                loadComponent: () => import('./app/pages/seo/seo-onboarding.component').then(m => m.SeoOnboardingComponent)
            },

            { path: 'uikit', loadChildren: () => import('./app/pages/uikit/uikit.routes') },
            { path: 'documentation', component: Documentation },
            { path: 'pages', loadChildren: () => import('./app/pages/pages.routes') }
        ]
    },
    {
        path: 'website-template',
        loadComponent: () =>
            import('./app/pages/website-template/website-template').then((m) => m.WebsiteTemplateComponent)
    },
    { path: 'landing', component: Landing },
    { path: 'notfound', component: Notfound },

    // 🔓 PUBLIC CASE-STATUS TRACKER (no login) — /case-status/<caseId>
    {
        path: 'case-status/:caseId',
        loadComponent: () =>
            import('./app/pages/case-status/case-status.component')
                .then(m => m.CaseStatusComponent)
    },

    // ✍️ PUBLIC DOCUMENT SIGNING (no login) — /sign/<token>
    // The token in the URL is the whole credential; the page can only read that one document,
    // request a code to the signer's own phone, and sign once.
    {
        path: 'sign/:token',
        loadComponent: () =>
            import('./app/pages/document-sign/document-sign.component')
                .then(m => m.DocumentSignComponent)
    },

    // 🌍 PUBLIC SITE — http://localhost:4300/<subdomain>
    // Must come AFTER all known top-level paths so reserved names win.
    {
        path: ':subdomain',
        loadComponent: () =>
            import('./app/pages/website-builder/public-site/public-site.component')
                .then(m => m.PublicSiteComponent),
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./app/pages/website-builder/public-site/home/public-site-home.component')
                        .then(m => m.PublicSiteHomeComponent)
            },
            {
                path: 'city/:cityId',
                loadComponent: () =>
                    import('./app/pages/website-builder/public-site/city-detail/city-detail.component')
                        .then(m => m.PublicSiteCityDetailComponent)
            },
            {
                path: 'property/:propertyId',
                loadComponent: () =>
                    import('./app/pages/website-builder/public-site/property-detail/property-detail.component')
                        .then(m => m.PublicSitePropertyDetailComponent)
            }
        ]
    },

    { path: '**', redirectTo: '/notfound' }
];
